
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { legacyApi, SaleOrder, DeliveryNoteSummary } from '../../../services/legacyApi';
import { ArrowLeft, Save, Truck, AlertTriangle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { clampShipQty, computeRemainingQty } from '../utils/deliveryMath';
import { FlowCoachPanel } from '../../../components/workflow/FlowCoachPanel';
import { WorkflowRightPanel } from '../../../components/workflow/WorkflowRightPanel';
import { WorkflowRightSidebar } from '../../../components/workflow/WorkflowRightSidebar';

interface DeliveryRow {
    so_detail_id?: number;
    estimate_part_id?: number;
    part_name: string;
    ordered_qty: number;
    delivered_qty: number; // Dispatched qty only
    allocated_qty: number; // Draft + dispatched qty already captured in DNs
    remaining_qty: number;
    current_ship_qty: number; // Input
}

export const CreateDelivery: React.FC = () => {
    const { soId } = useParams<{ soId: string }>();
    const navigate = useNavigate();

    const [so, setSo] = useState<SaleOrder | null>(null);
    const [history, setHistory] = useState<DeliveryNoteSummary[]>([]);
    const [items, setItems] = useState<DeliveryRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [remarks, setRemarks] = useState('');
    const [dnDate, setDnDate] = useState(new Date().toISOString().split('T')[0]);
    const [dnRef, setDnRef] = useState('');
    const [deliveryLocation, setDeliveryLocation] = useState('');
    const [poRef, setPoRef] = useState('');
    const [createdBy, setCreatedBy] = useState('System User');

    useEffect(() => {
        if (!soId) return;

        const loadData = async () => {
            try {
                setLoading(true);
                // 1. Fetch SO
                const soData = await legacyApi.sales.getOrder(parseInt(soId));
                setSo(soData);
                setPoRef(soData.customer_po_no || '');
                const deliveryHistory = await legacyApi.sales.getDeliveriesForOrder(parseInt(soId));
                setHistory(deliveryHistory);

                const draftReservedByDetail = new Map<number, number>();
                const draftReservedByPart = new Map<number, number>();

                const draftLikeDeliveries = deliveryHistory.filter((dn) => ![2, 3].includes(Number(dn.status_id || 1)));
                if (draftLikeDeliveries.length > 0) {
                    const details = await Promise.all(
                        draftLikeDeliveries.map(async (dn) => {
                            try {
                                return await legacyApi.sales.getDetail(Number(dn.dn_id));
                            } catch {
                                return null;
                            }
                        })
                    );
                    for (const dn of details) {
                        if (!dn) continue;
                        for (const item of dn.items || []) {
                            const qty = Number(item.quantity_shipped || 0);
                            if (qty <= 0) continue;
                            if (item.so_detail_id !== undefined && item.so_detail_id !== null) {
                                const key = Number(item.so_detail_id);
                                draftReservedByDetail.set(key, (draftReservedByDetail.get(key) || 0) + qty);
                            }
                            if (item.estimate_part_id !== undefined && item.estimate_part_id !== null) {
                                const key = Number(item.estimate_part_id);
                                draftReservedByPart.set(key, (draftReservedByPart.get(key) || 0) + qty);
                            }
                        }
                    }
                }

                // Construct Grid
                if (soData.details) {
                    const rows: DeliveryRow[] = soData.details.map((d: any) => {
                        const ordered = d.quantity || 0;
                        const delivered = Number(d.delivered_qty || 0);
                        const soDetailId = d.so_detail_id !== undefined && d.so_detail_id !== null ? Number(d.so_detail_id) : null;
                        const estimatePartId = d.estimate_part_id !== undefined && d.estimate_part_id !== null ? Number(d.estimate_part_id) : null;
                        const draftReserved = soDetailId !== null
                            ? Number(draftReservedByDetail.get(soDetailId) || 0)
                            : Number(draftReservedByPart.get(Number(estimatePartId || 0)) || 0);
                        const remainingFromOrder = Number.isFinite(Number(d.remaining_qty))
                            ? Number(d.remaining_qty)
                            : computeRemainingQty(ordered, delivered);
                        const allocated = delivered + draftReserved;
                        const remaining = computeRemainingQty(remainingFromOrder, draftReserved);

                        return {
                            so_detail_id: soDetailId === null ? undefined : soDetailId,
                            estimate_part_id: estimatePartId === null ? undefined : estimatePartId,
                            part_name: d.part_name,
                            ordered_qty: ordered,
                            delivered_qty: delivered,
                            allocated_qty: allocated,
                            remaining_qty: remaining,
                            current_ship_qty: remaining // Default to ship remaining
                        };
                    });
                    setItems(rows);
                }

            } catch (err: any) {
                setError(err.message || "Failed to load order");
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [soId]);

    const handleQtyChange = (index: number, val: string) => {
        const num = parseFloat(val) || 0;
        const newItems = [...items];
        const safeQty = clampShipQty(num, newItems[index].remaining_qty);
        newItems[index].current_ship_qty = safeQty;
        setItems(newItems);
    };

    const handleSubmit = async () => {
        if (!so) return;

        // Validation
        const toShip = items.filter(i => i.current_ship_qty > 0);
        if (toShip.length === 0) {
            setError("Please enter quantity to ship for at least one item.");
            return;
        }

        try {
            setSubmitting(true);
            setError(null);

            const res = await legacyApi.sales.createDelivery({
                so_id: so.so_id,
                dn_date: dnDate,
                created_by: createdBy,
                remarks: remarks,
                dn_ref: dnRef,
                delivery_location: deliveryLocation,
                po_ref: poRef,
                items: toShip.map(i => ({
                    so_detail_id: i.so_detail_id,
                    estimate_part_id: i.estimate_part_id,
                    quantity_shipped: i.current_ship_qty
                }))
            });
            console.log('CreateDelivery response:', res);

            if (res && res.dn_id) {
                navigate(`/legacy/delivery/${res.dn_id}`);
            } else {
                navigate(`/legacy/orders/${so.so_id}`);
            }

        } catch (err: any) {
            console.error('CreateDelivery error:', err);
            setError(err.message || "Failed to create delivery note");
            setSubmitting(false);
        }
    };

    if (loading) return <div className="p-8">Loading...</div>;
    if (!so) return <div className="p-8">Order not found</div>;

    return (
        <div className="space-y-6 max-w-5xl mx-auto p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => navigate(`/legacy/orders/${so.so_id}`)}>
                        <ArrowLeft className="h-4 w-4 mr-2" /> Back
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight text-foreground">Create Delivery Note</h1>
                        <p className="text-muted-foreground">
                            Dispatching items for Sale Order {`HC/SO/${String(so.so_id).padStart(4, '0')}`}
                        </p>
                    </div>
                </div>
                <Button onClick={handleSubmit} disabled={submitting}>
                    {submitting ? "Creating..." : <><Save className="mr-2 h-4 w-4" /> Generate DN</>}
                </Button>
            </div>

            {error && (
                <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            <div className="space-y-6">
                <div className="space-y-6">
                    {/* Header Form */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Delivery Details</CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <Label>Dispatch Date</Label>
                                <Input type="date" value={dnDate} onChange={e => setDnDate(e.target.value)} />
                            </div>
                            <div className="space-y-2">
                                <Label>PO Ref#</Label>
                                <Input value={poRef} onChange={e => setPoRef(e.target.value)} />
                            </div>
                            <div className="space-y-2">
                                <Label>DN Ref#</Label>
                                <Input value={dnRef} onChange={e => setDnRef(e.target.value)} />
                            </div>
                            <div className="space-y-2">
                                <Label>Created By</Label>
                                <Input value={createdBy} onChange={e => setCreatedBy(e.target.value)} />
                            </div>
                            <div className="col-span-2 space-y-2">
                                <Label>Remarks</Label>
                                <Input
                                    placeholder="Driver name, vehicle no, etc."
                                    value={remarks}
                                    onChange={e => setRemarks(e.target.value)}
                                />
                            </div>
                            <div className="col-span-2 space-y-2">
                                <Label>Delivery Location</Label>
                                <textarea
                                    className="min-h-[90px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                    placeholder="Delivery address / location"
                                    value={deliveryLocation}
                                    onChange={e => setDeliveryLocation(e.target.value)}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>
                <WorkflowRightSidebar mode="overlay" title="Delivery Workflow & Status">
                    <WorkflowRightPanel
                        title="Workflow & Status"
                        description="Source context, status, and prechecks"
                    >
                        <FlowCoachPanel entity="SO" entityId={so.so_id} />
                    </WorkflowRightPanel>
                </WorkflowRightSidebar>
            </div>

            {/* Items Grid */}
            <Card>
                <CardHeader>
                    <CardTitle>Items to Ship</CardTitle>
                </CardHeader>
                <CardContent>
                    {history.some((dn) => ![2, 3].includes(Number(dn.status_id || 1))) && (
                        <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                            Existing draft delivery notes already reserve quantities. Balance below reflects dispatched quantities plus any still-open draft reservations.
                        </div>
                    )}
                    <div className="border rounded-md">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-muted text-muted-foreground">
                                <tr>
                                    <th className="p-3 font-medium">Part Name</th>
                                    <th className="p-3 font-medium text-right">Ordered</th>
                                    <th className="p-3 font-medium text-right">Dispatched</th>
                                    <th className="p-3 font-medium text-right">Allocated</th>
                                    <th className="p-3 font-medium text-right">Balance</th>
                                    <th className="p-3 font-medium text-right w-32">Ship Now</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {items.map((item, idx) => (
                                    <tr key={idx} className={`hover:bg-muted/50 ${item.remaining_qty === 0 ? 'opacity-50 bg-gray-50' : ''}`}>
                                        <td className="p-3">
                                            {item.part_name}
                                            {item.remaining_qty === 0 && item.delivered_qty >= item.ordered_qty && (
                                                <span className="ml-2 text-xs font-medium text-green-600">(Completed)</span>
                                            )}
                                            {item.remaining_qty === 0 && item.delivered_qty < item.ordered_qty && item.allocated_qty > item.delivered_qty && (
                                                <span className="ml-2 text-xs font-medium text-amber-600">(Reserved in draft DN)</span>
                                            )}
                                        </td>
                                        <td className="p-3 text-right">{item.ordered_qty}</td>
                                        <td className="p-3 text-right text-gray-500">{item.delivered_qty}</td>
                                        <td className="p-3 text-right text-gray-500">{item.allocated_qty}</td>
                                        <td className="p-3 text-right font-medium">{item.remaining_qty}</td>
                                        <td className="p-3 text-right">
                                            <Input
                                                type="number"
                                                min="0"
                                                max={item.remaining_qty}
                                                disabled={item.remaining_qty === 0}
                                                className={`h-8 text-right ${item.current_ship_qty > item.remaining_qty ? 'border-red-500 bg-red-50' : ''}`}
                                                value={item.current_ship_qty}
                                                onChange={e => handleQtyChange(idx, e.target.value)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};
